import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { formatCurrency } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import { Plus, Truck, DollarSign, Trash2, Search } from "lucide-react";

const API = `${window.location.origin}/api`;

export default function SuppliersPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    email: "",
    shop_name: "",
  });
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, formData);
      toast.success(language === "ar" ? "تم الإضافة بنجاح" : "Added successfully");
      fetchSuppliers();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedSupplier) return;
    try {
      await axios.post(`${API}/suppliers/${selectedSupplier.id}/payment`, {
        supplier_id: selectedSupplier.id,
        amount: parseFloat(paymentAmount),
        notes: paymentNotes,
      });
      toast.success(language === "ar" ? "تم تسجيل الدفع" : "Payment recorded");
      fetchSuppliers();
      setIsPaymentDialogOpen(false);
      setPaymentAmount("");
      setPaymentNotes("");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("error"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === "ar" ? "هل أنت متأكد؟" : "Are you sure?")) return;
    try {
      await axios.delete(`${API}/suppliers/${id}`);
      toast.success(language === "ar" ? "تم الحذف" : "Deleted");
      fetchSuppliers();
    } catch (error) {
      toast.error(t("error"));
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setFormData({ name: "", phone: "", address: "", email: "", shop_name: "" });
  };

  const openPaymentDialog = (supplier) => {
    setSelectedSupplier(supplier);
    setIsPaymentDialogOpen(true);
  };

  const totalDebt = suppliers.reduce((sum, s) => sum + (s.debt || 0), 0);

  // تصفية الموردين بناءً على البحث
  const filteredSuppliers = suppliers.filter(
    (s) =>
      s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.phone?.includes(searchTerm) ||
      s.shop_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">{t("suppliers")}</h1>
          <p className="text-slate-400 mt-1">
            {language === "ar" ? "إجمالي المستحقات:" : "Total Owed:"} {formatCurrency(totalDebt)}
            <USDHint amount={totalDebt} inline className="ml-2" />
          </p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-red-600 hover:bg-red-700 btn-lift"
          data-testid="add-supplier-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          {t("addSupplier")}
        </Button>
      </div>

      {/* Search Box */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400`} />
            <Input
              placeholder={language === "ar" ? "بحث بالاسم، الهاتف، أو اسم المتجر..." : "Search by name, phone, or shop name..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${language === "ar" ? "pr-10" : "pl-10"} bg-slate-900 border-slate-700 text-white`}
              data-testid="supplier-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{searchTerm ? (language === "ar" ? "لا توجد نتائج" : "No results") : t("noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500">{t("supplierName")}</TableHead>
                    <TableHead className="text-amber-500">{t("shopName")}</TableHead>
                    <TableHead className="text-amber-500">{t("phone")}</TableHead>
                    <TableHead className="text-amber-500">{t("debt")}</TableHead>
                    <TableHead className="text-amber-500">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSuppliers.map((supplier) => (
                    <TableRow key={supplier.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="text-white font-medium">{supplier.name}</TableCell>
                      <TableCell className="text-slate-300">{supplier.shop_name || "-"}</TableCell>
                      <TableCell className="text-slate-300 font-mono">{supplier.phone}</TableCell>
                      <TableCell>
                        <span className={supplier.debt > 0 ? "text-orange-500 font-bold" : "text-green-500"}>
                          {formatCurrency(supplier.debt || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {supplier.debt > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPaymentDialog(supplier)}
                              className="border-green-600 text-green-500 hover:bg-green-600 hover:text-white"
                            >
                              <DollarSign className="w-4 h-4 me-1" />
                              {language === "ar" ? "دفع" : "Pay"}
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(supplier.id)}
                            className="text-slate-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Supplier Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{t("addSupplier")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-200">{t("supplierName")}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                required
                data-testid="supplier-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("shopName")}</Label>
              <Input
                value={formData.shop_name}
                onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="supplier-shop-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("phone")}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                required
                data-testid="supplier-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("address")}</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-supplier-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {language === "ar" ? "دفع للمورد" : "Pay Supplier"} - {selectedSupplier?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayment} className="space-y-4">
            <div className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
              <p className="text-slate-400 text-sm">{language === "ar" ? "المبلغ المستحق" : "Amount Owed"}</p>
              <p className="text-2xl font-bold text-orange-500">{formatCurrency(selectedSupplier?.debt || 0)}</p>
              <USDHint amount={selectedSupplier?.debt || 0} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("amount")}</Label>
              <PriceInput
                value={parseFloat(paymentAmount) || 0}
                onChange={(v) => setPaymentAmount(String(v))}
                placeholder="0"
                testId="supplier-payment-amount-input"
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200">{t("notes")}</Label>
              <Input
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700">
                {t("confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
